import React from 'react';
import { motion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { GraduationCap, Award, Languages as LangIcon } from 'lucide-react';

const Education = () => {
  const { t } = useLanguage();

  const courses = t('education.courses');
  const languages = t('languages.items');

  return (
    <section id="education" className="py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Education & Courses */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-display font-black mb-12 tracking-tight flex items-center gap-4">
                <GraduationCap className="w-12 h-12 text-primary" />
                {t('education.title')}
              </h2>

              <div className="p-8 glass rounded-3xl border-l-4 border-primary hover:border-primary/50 transition-all mb-12">
                <h3 className="text-2xl font-display font-bold mb-2">{t('education.degree')}</h3>
                <p className="text-primary font-mono font-bold mb-4">{t('education.period')}</p>
                <div className="w-12 h-1 bg-primary/30 rounded-full" />
              </div>

              <h3 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
                <Award className="text-secondary" />
                {t('education.courses_title')}
              </h3>
              <div className="space-y-4">
                {courses.map((course: string, idx: number) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-4 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-colors"
                  >
                    <p className="text-white/80 font-medium">{course}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Languages */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-display font-black mb-12 tracking-tight flex items-center gap-4">
                <LangIcon className="w-12 h-12 text-secondary" />
                {t('languages.title')}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {languages.map((lang: any, idx: number) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.2 }}
                    className="p-8 glass rounded-3xl text-center group hover:border-secondary/30 transition-all"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-secondary/20 transition-colors">
                      <span className="text-2xl font-black text-secondary">{lang.name.charAt(0)}</span>
                    </div>
                    <h3 className="text-xl font-display font-bold mb-2">{lang.name}</h3>
                    <p className="text-sm font-mono text-white/40 uppercase tracking-widest">{lang.level}</p>
                    
                    {/* Visual Progress Bar */}
                    <div className="mt-6 h-1 w-full bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: lang.name === 'Español' || lang.name === 'Spanish' ? '100%' : '75%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.5, delay: 0.5 }}
                        className="h-full bg-secondary"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;
